import i18n from "../i18n";
import React from "react";
import _ from "lodash";
import { WalletRow } from "./payrollTable";
import {
  Accordion,
  Table,
  Text,
  Badge,
  Stack,
  Group,
  Anchor,
  Tooltip,
} from "@mantine/core";
import { useFormatter } from "../hooks/useFomatter";
import moment from "moment";
import { formatCommissionCategory } from "../helpers/payrollBidDisplay";
import { incentiveTypeBadge } from "../data/options";

const getStatusColor = (status) => {
  switch (status) {
    case "SETTLED":
      return "green";
    case "HOLD":
      return "yellow";
    case "PENDING":
    default:
      return "gray";
  }
};

const isIncentiveBid = (bid) =>
  bid.sourceType === "INCENTIVE" || !!bid.incentive;

const isAdjustmentBid = (bid) => bid.sourceType === "ADJUSTMENT";

const getSourceLabel = (bid) => {
  if (isAdjustmentBid(bid)) return "Adjustment";
  if (bid.sourceType === "CLIENTPAYMENT" || bid.debitNote) return "Client Payment";
  if (bid.sourceType === "COMMISSIONSTATEMENT" || bid.order)
    return "Comm. Statement";
  if (isIncentiveBid(bid)) {
    return incentiveTypeBadge(bid.incentive?.incentiveType).label;
  }
  return bid.sourceType;
};

const getSourceColor = (bid) => {
  if (isAdjustmentBid(bid)) return "red";
  if (bid.sourceType === "CLIENTPAYMENT" || bid.debitNote) return "blue";
  if (bid.sourceType === "COMMISSIONSTATEMENT" || bid.order) return "violet";
  if (isIncentiveBid(bid)) {
    return incentiveTypeBadge(bid.incentive?.incentiveType).color;
  }
  return "gray";
};

const getLinkedOrder = (bid) => bid.order || bid.incentive?.order || null;

const getLinkedDebitNote = (bid) =>
  bid.debitNote || bid.incentive?.debitNote || null;

const getBidProductLabel = (bid, product, productType, isDnBid) => {
  if (isIncentiveBid(bid)) {
    return bid.incentive?.title || bid.incentive?.code || null;
  }
  if (isDnBid) {
    return productType?.name || product?.name || null;
  }
  return product?.name || productType?.name || null;
};

const getBidRemark = (bid) => {
  const description =
    typeof bid.description === "string" ? bid.description.trim() : "";

  let sourceRemark = "";
  if (bid.sourceType === "COMMISSIONSTATEMENT" && bid.consultantRemark) {
    sourceRemark = String(bid.consultantRemark).trim();
  } else if (isIncentiveBid(bid)) {
    const parts = [bid.incentive?.description, bid.incentive?.remark].filter(
      Boolean,
    );
    sourceRemark = parts.length
      ? parts.join(" — ")
      : String(bid.remark || "").trim();
  } else if (isAdjustmentBid(bid)) {
    sourceRemark = String(bid.remark || "").trim();
  }

  if (description && sourceRemark && description !== sourceRemark) {
    return `${description} / ${sourceRemark}`;
  }
  return description || sourceRemark || null;
};

const RemarkCell = ({ text }) => {
  if (!text) {
    return (
      <Text size="xs" color="dimmed">
        -
      </Text>
    );
  }

  return (
    <Tooltip label={text} withArrow multiline maw={420}>
      <Text size="xs" color="dimmed" italic lineClamp={3}>
        {text}
      </Text>
    </Tooltip>
  );
};

const PayrollBidTable = ({ bids }) => {
  const [formatter] = useFormatter();

  if (!bids || bids.length === 0) {
    return (
      <Text size="sm" color="dimmed">
        No bids found.
      </Text>
    );
  }

  return (
    <Table fontSize="xs" striped highlightOnHover withBorder>
      <thead>
        <tr>
          <th style={{ width: 40, textAlign: "center" }}>#</th>
          <th style={{ minWidth: 180 }}>Bid</th>
          <th style={{ minWidth: 100 }}>{i18n.t("Order")}</th>
          <th style={{ minWidth: 100 }}>{i18n.t("Debit Note")}</th>
          <th style={{ minWidth: 120 }}>{i18n.t("Client")}</th>
          <th style={{ minWidth: 100 }}>{i18n.t("Provider")}</th>
          <th style={{ minWidth: 120 }}>{i18n.t("Product")}</th>
          <th style={{ minWidth: 120 }}>{i18n.t("Commission Category")}</th>
          <th style={{ minWidth: 140 }}>{i18n.t("Remark")}</th>
          <th style={{ width: 120, textAlign: "right" }}>{i18n.t("Amount")}</th>
        </tr>
      </thead>
      <tbody>
        {bids.map((bid, index) => {
          const order = getLinkedOrder(bid);
          const debitNote = getLinkedDebitNote(bid);
          const client = debitNote?.client || order?.client;
          const isDnBid =
            debitNote &&
            (bid.sourceType === "CLIENTPAYMENT" || isAdjustmentBid(bid));
          const productType =
            debitNote?.productType ||
            order?.productType ||
            debitNote?.order?.productType;
          const productRef =
            debitNote?.product ||
            order?.product ||
            debitNote?.order?.product;
          const product = isDnBid
            ? productType || productRef
            : productRef || productType;
          const provider =
            debitNote?.provider ||
            order?.provider ||
            debitNote?.order?.provider;
          const productLabel = getBidProductLabel(
            bid,
            product,
            productType,
            isDnBid,
          );
          const remark = getBidRemark(bid);

          return (
            <tr key={bid._id || index}>
              <td style={{ textAlign: "center" }}>
                <Text size="xs" color="dimmed">
                  {index + 1}
                </Text>
              </td>
              <td>
                <Group noWrap spacing={1}>
                  <Badge
                    size="xs"
                    color={getStatusColor(bid.status)}
                    variant="filled"
                    radius="0"
                  >
                    {bid.status}
                  </Badge>
                  <Badge
                    size="xs"
                    color={getSourceColor(bid)}
                    variant="light"
                    radius="0"
                  >
                    {getSourceLabel(bid)}
                  </Badge>
                </Group>
                <Text size="xs" weight={500}>
                  {bid.code}
                </Text>
                {isIncentiveBid(bid) && bid.incentive?.code && (
                  <Anchor
                    href={`/m/account/incentive?id=${
                      bid.incentive._id || bid.incentive
                    }&mode=edit`}
                    target="_blank"
                    size="xs"
                    color="teal"
                  >
                    {bid.incentive.code}
                  </Anchor>
                )}
                {isIncentiveBid(bid) && bid.incentive?.issueDate && (
                  <Text size="xs" color="dimmed">
                    {moment(bid.incentive.issueDate).format("YYYY-MM-DD")}
                  </Text>
                )}
              </td>
              <td>
                {order ? (
                  <Anchor
                    href={`/order?id=${order._id || order}&mode=edit`}
                    target="_blank"
                    size="xs"
                  >
                    {order.code || "-"}
                  </Anchor>
                ) : (
                  <Text size="xs" color="dimmed">
                    -
                  </Text>
                )}
              </td>
              <td>
                {debitNote ? (
                  <Anchor
                    href={`/m/gi/debitNote?id=${
                      debitNote._id || debitNote
                    }&mode=edit`}
                    target="_blank"
                    size="xs"
                  >
                    {debitNote.code || "-"}
                  </Anchor>
                ) : (
                  <Text size="xs" color="dimmed">
                    -
                  </Text>
                )}
              </td>
              <td>
                {client ? (
                  <Stack spacing={0}>
                    <Text size="xs" weight={500} lineClamp={1}>
                      {client.name || client.code}
                    </Text>
                    {client.cname && (
                      <Text size="xs" color="dimmed" lineClamp={1}>
                        {client.cname}
                      </Text>
                    )}
                  </Stack>
                ) : (
                  <Text size="xs" color="dimmed">
                    -
                  </Text>
                )}
              </td>
              <td>
                {provider ? (
                  <Text size="xs" weight={500} lineClamp={1}>
                    {provider.shortName || provider.name || provider.code}
                  </Text>
                ) : (
                  <Text size="xs" color="dimmed">
                    -
                  </Text>
                )}
              </td>
              <td>
                {productLabel ? (
                  <Stack spacing={0}>
                    <Text size="xs" weight={500} lineClamp={1}>
                      {productLabel}
                    </Text>
                    {isIncentiveBid(bid) && bid.incentive?.description && (
                      <Text size="xs" color="dimmed" lineClamp={2}>
                        {bid.incentive.description}
                      </Text>
                    )}
                    {!isIncentiveBid(bid) && product?.cname && (
                      <Text size="xs" color="dimmed" lineClamp={1}>
                        {product.cname}
                      </Text>
                    )}
                  </Stack>
                ) : (
                  <Text size="xs" color="dimmed">
                    -
                  </Text>
                )}
              </td>
              <td>
                <Text size="xs">
                  {formatCommissionCategory(bid.commissionCategory, i18n.t) ||
                    "-"}
                </Text>
              </td>
              <td>
                <RemarkCell text={remark} />
              </td>
              <td style={{ textAlign: "right" }}>
                <Text
                  size="xs"
                  weight={600}
                  color={bid.commissionAmount >= 0 ? "teal" : "red"}
                >
                  {formatter.currency(bid.commissionAmount)}
                </Text>
              </td>
            </tr>
          );
        })}
      </tbody>
    </Table>
  );
};

const PayrollPaymentBids = ({ form }) => {
  const { _id, payrollBids, bids, payroll } = form.values;
  const hasPayrollBids = payrollBids && payrollBids.length > 0;
  const hasLegacyBids = bids && bids.length > 0;
  const isPayroll2Payment = Array.isArray(payrollBids) && !!payroll;

  if (!_id) return null;

  // New payroll2 records: use payrollBids
  if (hasPayrollBids) {
    return <PayrollBidTable bids={payrollBids} />;
  }

  // Old records: fallback to WalletRow
  if (hasLegacyBids) {
    return (
      <Accordion variant="separated" defaultValue={_id}>
        <WalletRow paymentId={_id} />
      </Accordion>
    );
  }

  return (
    <Text size="sm" color="dimmed">
      {isPayroll2Payment
        ? "No payroll2 bid details found."
        : "No bids found."}
    </Text>
  );
};

export default PayrollPaymentBids;
